import { createEl } from '../lib/dom';
import { publicAsset } from '../lib/assets';
import {
  fetchBusinessTypes,
  registerBusiness,
  validatePresentationImage,
  type BusinessTypeOption,
} from '../lib/business-register-api';
import { searchLocationSuggestions, type LocationSuggestion } from '../lib/location-autocomplete';

const TERMS_HREF = publicAsset('terms.html#terminos');
const POLICY_HREF = publicAsset('terms.html#politica');
const LOGIN_HREF = publicAsset('business.html');

function digitsOnly(value: string, maxLen: number): string {
  return value.replace(/\D/g, '').slice(0, maxLen);
}

function field(
  id: string,
  label: string,
  input: HTMLElement,
  hint?: string
): HTMLElement {
  const children: HTMLElement[] = [
    createEl('label', { className: 'sv-biz-reg__label', for: id }, [label]),
    input,
  ];
  if (hint) {
    children.push(createEl('p', { className: 'sv-biz-reg__hint' }, [hint]));
  }
  return createEl('div', { className: 'sv-biz-reg__field' }, children);
}

function showFieldError(input: HTMLElement, message: string | null): void {
  const wrap = input.closest('.sv-biz-reg__field');
  if (!wrap) {
    return;
  }
  const existing = wrap.querySelector('.sv-biz-reg__error');
  existing?.remove();
  if (message) {
    wrap.appendChild(createEl('p', { className: 'sv-biz-reg__error', role: 'alert' }, [message]));
    input.setAttribute('aria-invalid', 'true');
  } else {
    input.removeAttribute('aria-invalid');
  }
}

type BizRegModalKind = 'confirm' | 'loading' | 'success';

function createBizRegModalRoot(): HTMLElement {
  const root = createEl('div', {
    className: 'sv-biz-reg-modal',
    hidden: true,
    role: 'dialog',
    'aria-modal': 'true',
  });
  root.appendChild(
    createEl('div', { className: 'sv-biz-reg-modal__backdrop', 'data-biz-reg-modal-close': 'backdrop' })
  );
  root.appendChild(createEl('div', { className: 'sv-biz-reg-modal__panel' }));
  document.body.appendChild(root);
  return root;
}

function setBizRegModal(
  root: HTMLElement,
  kind: BizRegModalKind,
  opts: { title: string; message: string; onConfirm?: () => void; onClose?: () => void }
): void {
  const panel = root.querySelector('.sv-biz-reg-modal__panel') as HTMLElement;
  panel.replaceChildren();

  const titleId = 'sv-biz-reg-modal-title';
  panel.appendChild(createEl('h4', { className: 'sv-biz-reg-modal__title', id: titleId }, [opts.title]));
  panel.appendChild(createEl('p', { className: 'sv-biz-reg-modal__message' }, [opts.message]));

  root.setAttribute('aria-labelledby', titleId);

  if (kind === 'loading') {
    panel.appendChild(
      createEl('div', { className: 'sv-biz-reg-modal__loader', 'aria-hidden': 'true' }, [
        createEl('span', { className: 'sv-biz-reg-modal__spinner' }),
      ])
    );
    root.dataset.kind = 'loading';
    root.hidden = false;
    document.body.classList.add('sv-biz-reg-modal-open');
    return;
  }

  const actions = createEl('div', { className: 'sv-biz-reg-modal__actions' });

  if (kind === 'confirm') {
    const cancelBtn = createEl(
      'button',
      { type: 'button', className: 'sv-btn sv-btn--outline-dark sv-biz-reg-modal__btn' },
      ['Cancelar']
    ) as HTMLButtonElement;
    const confirmBtn = createEl(
      'button',
      { type: 'button', className: 'sv-btn sv-btn--primary sv-biz-reg-modal__btn' },
      ['Confirmar envío']
    ) as HTMLButtonElement;

    const close = (): void => {
      hideBizRegModal(root);
      opts.onClose?.();
    };

    cancelBtn.addEventListener('click', close);
    confirmBtn.addEventListener('click', () => {
      hideBizRegModal(root);
      opts.onConfirm?.();
    });

    actions.append(cancelBtn, confirmBtn);
  } else {
    const okBtn = createEl(
      'button',
      { type: 'button', className: 'sv-btn sv-btn--primary sv-biz-reg-modal__btn sv-biz-reg-modal__btn--full' },
      ['Entendido']
    ) as HTMLButtonElement;
    okBtn.addEventListener('click', () => {
      hideBizRegModal(root);
      opts.onClose?.();
    });
    actions.appendChild(okBtn);
  }

  panel.appendChild(actions);
  root.dataset.kind = kind;
  root.hidden = false;
  document.body.classList.add('sv-biz-reg-modal-open');
}

function hideBizRegModal(root: HTMLElement): void {
  root.hidden = true;
  root.removeAttribute('aria-labelledby');
  delete root.dataset.kind;
  if (!document.querySelector('.sv-biz-reg-modal:not([hidden])')) {
    document.body.classList.remove('sv-biz-reg-modal-open');
  }
}

function openConfirmModal(
  root: HTMLElement,
  onConfirm: () => void
): void {
  setBizRegModal(root, 'confirm', {
    title: 'Confirmar solicitud',
    message:
      '¿Confirmas el envío de la solicitud de activación de tu empresa? Revisaremos los datos antes de habilitar tu cuenta.',
    onConfirm,
  });
}

function openLoadingModal(root: HTMLElement): void {
  setBizRegModal(root, 'loading', {
    title: 'Enviando solicitud',
    message: 'Estamos registrando los datos de tu empresa. Por favor espera un momento.',
  });
}

function openSuccessModal(root: HTMLElement, onClose?: () => void): void {
  setBizRegModal(root, 'success', {
    title: 'Solicitud enviada',
    message:
      'Se ha enviado la solicitud y pronto la revisaremos y la autorizaremos. Atentamente, Soporte Servave.',
    onClose,
  });
}

export function renderBusinessRegisterForm(target: HTMLElement): void {
  const statusBox = createEl('div', {
    className: 'sv-biz-reg__status',
    hidden: true,
    role: 'status',
  });

  const nameInput = createEl('input', {
    className: 'sv-biz-reg__input',
    type: 'text',
    id: 'sv-biz-reg-name',
    name: 'name',
    maxlength: '60',
    required: true,
    autocomplete: 'organization',
    placeholder: 'Ej. Taller Servave Norte',
  }) as HTMLInputElement;

  const nitInput = createEl('input', {
    className: 'sv-biz-reg__input',
    type: 'text',
    id: 'sv-biz-reg-nit',
    name: 'nit',
    inputMode: 'numeric',
    maxlength: '10',
    required: true,
    autocomplete: 'off',
    placeholder: '9001234567',
  }) as HTMLInputElement;

  const locationInput = createEl('input', {
    className: 'sv-biz-reg__input',
    type: 'text',
    id: 'sv-biz-reg-location',
    name: 'location',
    maxlength: '60',
    required: true,
    autocomplete: 'street-address',
    placeholder: 'Ciudad, barrio o dirección',
    role: 'combobox',
    'aria-autocomplete': 'list',
    'aria-expanded': 'false',
    'aria-controls': 'sv-biz-reg-location-list',
  }) as HTMLInputElement;

  const locationList = createEl('ul', {
    className: 'sv-biz-reg__suggestions',
    id: 'sv-biz-reg-location-list',
    role: 'listbox',
    hidden: true,
  });

  const phoneInput = createEl('input', {
    className: 'sv-biz-reg__input',
    type: 'tel',
    id: 'sv-biz-reg-phone',
    name: 'telephone',
    inputMode: 'numeric',
    maxlength: '10',
    required: true,
    autocomplete: 'tel',
    placeholder: '3001234567',
  }) as HTMLInputElement;

  const passwordInput = createEl('input', {
    className: 'sv-biz-reg__input sv-biz-reg__input--password',
    type: 'password',
    id: 'sv-biz-reg-password',
    name: 'password',
    maxlength: '10',
    required: true,
    autocomplete: 'new-password',
    placeholder: 'Mínimo requerido por la app',
  }) as HTMLInputElement;

  const togglePwdBtn = createEl(
    'button',
    {
      type: 'button',
      className: 'sv-biz-reg__pwd-toggle',
      'aria-label': 'Mostrar contraseña',
    },
    ['Ver']
  ) as HTMLButtonElement;

  const passwordWrap = createEl('div', { className: 'sv-biz-reg__password-wrap' }, [
    passwordInput,
    togglePwdBtn,
  ]);

  const descriptionInput = createEl('textarea', {
    className: 'sv-biz-reg__textarea',
    id: 'sv-biz-reg-description',
    name: 'description',
    rows: '5',
    maxlength: '2000',
    required: true,
    placeholder: 'Ej. 1234567890, 9876543210, 1122334455',
  }) as HTMLTextAreaElement;

  const fileInput = createEl('input', {
    type: 'file',
    id: 'sv-biz-reg-image',
    name: 'imagen',
    className: 'sv-biz-reg__file-input',
    accept: 'image/png,image/jpeg,image/jpg',
    required: true,
  }) as HTMLInputElement;

  const fileLabel = createEl('span', { className: 'sv-biz-reg__file-label' }, [
    'Seleccionar imagen (PNG o JPG)',
  ]);
  const fileMeta = createEl('span', { className: 'sv-biz-reg__file-meta' }, ['Ningún archivo']);

  const fileTrigger = createEl('label', { className: 'sv-biz-reg__file-trigger', for: 'sv-biz-reg-image' }, [
    fileLabel,
    fileMeta,
  ]);

  const termsCheck = createEl('input', {
    type: 'checkbox',
    id: 'sv-biz-reg-terms',
    name: 'acceptTerms',
    required: true,
  }) as HTMLInputElement;

  const policyCheck = createEl('input', {
    type: 'checkbox',
    id: 'sv-biz-reg-policy',
    name: 'acceptDataPolicy',
    required: true,
  }) as HTMLInputElement;

  const submitBtn = createEl(
    'button',
    {
      type: 'submit',
      className: 'sv-btn sv-btn--primary sv-biz-reg__submit',
      'data-label-default': 'Solicitud de activación',
      'data-label-pending': 'Enviando solicitud…',
    },
    ['Solicitud de activación']
  ) as HTMLButtonElement;

  const form = createEl(
    'form',
    {
      className: 'sv-biz-reg__form',
      novalidate: true,
    },
    [
      createEl('div', { className: 'sv-biz-reg__grid' }, [
        field('sv-biz-reg-name', 'Nombre de la compañía *', nameInput),
        field('sv-biz-reg-nit', 'NIT o documento de identidad *', nitInput),
        createEl('div', { className: 'sv-biz-reg__field sv-biz-reg__field--location' }, [
          createEl('label', { className: 'sv-biz-reg__label', for: 'sv-biz-reg-location' }, ['Ubicación *']),
          createEl('div', { className: 'sv-biz-reg__location-wrap' }, [locationInput, locationList]),
        ]),
        field('sv-biz-reg-phone', 'Teléfono *', phoneInput),
        field('sv-biz-reg-password', 'Contraseña *', passwordWrap),
      ]),
      field(
        'sv-biz-reg-description',
        'Documentos de colaboradores asociados *',
        descriptionInput,
        'Indica aquí los números de documento de identidad de los colaboradores vinculados a tu empresa (uno por línea o separados por coma).'
      ),
      createEl('div', { className: 'sv-biz-reg__field' }, [
        createEl('span', { className: 'sv-biz-reg__label' }, ['Imagen de presentación *']),
        createEl('p', { className: 'sv-biz-reg__hint' }, [
          'Logo o imagen que represente tu marca. Formatos PNG o JPG.',
        ]),
        fileTrigger,
        fileInput,
      ]),
      createEl('fieldset', { className: 'sv-biz-reg__legal' }, [
        createEl('legend', { className: 'sv-biz-reg__legal-legend' }, ['Aceptación legal']),
        createEl('label', { className: 'sv-biz-reg__check' }, [
          termsCheck,
          createEl('span', { className: 'sv-biz-reg__check-text' }, [
            'He leído y acepto los ',
            createEl('a', {
              className: 'sv-biz-reg__link',
              href: TERMS_HREF,
              target: '_blank',
              rel: 'noopener noreferrer',
            }, ['términos y condiciones']),
            ' de uso.',
          ]),
        ]),
        createEl('label', { className: 'sv-biz-reg__check' }, [
          policyCheck,
          createEl('span', { className: 'sv-biz-reg__check-text' }, [
            'Autorizo el tratamiento de mis datos conforme la ',
            createEl('a', {
              className: 'sv-biz-reg__link',
              href: POLICY_HREF,
              target: '_blank',
              rel: 'noopener noreferrer',
            }, ['política de datos personales']),
            ', incluidos fines comerciales donde aplique.',
          ]),
        ]),
      ]),
      statusBox,
      submitBtn,
      createEl('p', { className: 'sv-biz-reg__footer-hint' }, [
        '¿Ya tienes cuenta activa? ',
        createEl('a', { className: 'sv-biz-reg__link', href: LOGIN_HREF }, ['Ingresar como empresa']),
      ]),
    ]
  ) as HTMLFormElement;

  let selectedLat: string | null = null;
  let selectedLon: string | null = null;
  let locationTimer: number | undefined;
  let businessTypes: BusinessTypeOption[] = [];
  let selectedImage: File | null = null;
  const modalRoot = createBizRegModalRoot();

  void fetchBusinessTypes().then((types) => {
    businessTypes = types;
  });

  function setStatus(kind: 'ok' | 'error' | null, message = ''): void {
    statusBox.classList.remove('sv-biz-reg__status--ok', 'sv-biz-reg__status--error');
    if (!kind || !message) {
      statusBox.hidden = true;
      statusBox.textContent = '';
      return;
    }
    statusBox.hidden = false;
    statusBox.textContent = message;
    statusBox.classList.add(kind === 'ok' ? 'sv-biz-reg__status--ok' : 'sv-biz-reg__status--error');
  }

  function renderSuggestions(items: LocationSuggestion[]): void {
    locationList.replaceChildren();
    if (items.length === 0) {
      locationList.hidden = true;
      locationInput.setAttribute('aria-expanded', 'false');
      return;
    }
    for (const item of items) {
      locationList.appendChild(
        createEl(
          'li',
          {
            className: 'sv-biz-reg__suggestion',
            role: 'option',
            tabIndex: '0',
          },
          [item.description]
        )
      );
      const li = locationList.lastElementChild as HTMLLIElement;
      li.addEventListener('mousedown', (ev) => {
        ev.preventDefault();
        locationInput.value = item.description;
        selectedLat = item.lat;
        selectedLon = item.lon;
        locationList.hidden = true;
        locationInput.setAttribute('aria-expanded', 'false');
        showFieldError(locationInput, null);
      });
    }
    locationList.hidden = false;
    locationInput.setAttribute('aria-expanded', 'true');
  }

  nitInput.addEventListener('input', () => {
    nitInput.value = digitsOnly(nitInput.value, 10);
  });
  phoneInput.addEventListener('input', () => {
    phoneInput.value = digitsOnly(phoneInput.value, 10);
  });

  locationInput.addEventListener('input', () => {
    selectedLat = null;
    selectedLon = null;
    window.clearTimeout(locationTimer);
    const value = locationInput.value;
    locationTimer = window.setTimeout(() => {
      void searchLocationSuggestions(value).then(renderSuggestions);
    }, 280);
  });

  locationInput.addEventListener('blur', () => {
    window.setTimeout(() => {
      locationList.hidden = true;
      locationInput.setAttribute('aria-expanded', 'false');
    }, 150);
  });

  togglePwdBtn.addEventListener('click', () => {
    const show = passwordInput.type === 'password';
    passwordInput.type = show ? 'text' : 'password';
    togglePwdBtn.textContent = show ? 'Ocultar' : 'Ver';
    togglePwdBtn.setAttribute('aria-label', show ? 'Ocultar contraseña' : 'Mostrar contraseña');
  });

  fileInput.addEventListener('change', () => {
    const file = fileInput.files?.[0] ?? null;
    selectedImage = null;
    if (!file) {
      fileMeta.textContent = 'Ningún archivo';
      return;
    }
    const err = validatePresentationImage(file);
    if (err) {
      setStatus('error', err);
      fileInput.value = '';
      fileMeta.textContent = 'Ningún archivo';
      return;
    }
    selectedImage = file;
    fileMeta.textContent = file.name;
    setStatus(null);
  });

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    setStatus(null);

    const name = nameInput.value.trim();
    const nit = nitInput.value.trim();
    const location = locationInput.value.trim();
    const telephone = phoneInput.value.trim();
    const password = passwordInput.value;
    const description = descriptionInput.value.trim();
    const acceptTerms = termsCheck.checked;
    const acceptDataPolicy = policyCheck.checked;

    let valid = true;
    if (!name) {
      showFieldError(nameInput, 'El campo es requerido.');
      valid = false;
    } else {
      showFieldError(nameInput, null);
    }
    if (!nit) {
      showFieldError(nitInput, 'El campo es requerido.');
      valid = false;
    } else {
      showFieldError(nitInput, null);
    }
    if (!location) {
      showFieldError(locationInput, 'El campo es requerido.');
      valid = false;
    } else {
      showFieldError(locationInput, null);
    }
    if (!telephone) {
      showFieldError(phoneInput, 'El campo es requerido.');
      valid = false;
    } else {
      showFieldError(phoneInput, null);
    }
    if (!password) {
      showFieldError(passwordInput, 'El campo es requerido.');
      valid = false;
    } else {
      showFieldError(passwordInput, null);
    }
    if (!description) {
      showFieldError(descriptionInput, 'El campo es requerido.');
      valid = false;
    } else {
      showFieldError(descriptionInput, null);
    }
    if (!selectedImage) {
      setStatus('error', 'Debes adjuntar la imagen de presentación.');
      valid = false;
    }
    if (!acceptTerms || !acceptDataPolicy) {
      setStatus('error', 'Debes aceptar los términos y la política de datos.');
      valid = false;
    }
    if (!valid) {
      return;
    }

    openConfirmModal(modalRoot, () => {
      submitBtn.disabled = true;
      openLoadingModal(modalRoot);

      void (async () => {
        const result = await registerBusiness(
          {
            name,
            nit,
            telephone,
            location,
            password,
            description,
            lat: selectedLat,
            lon: selectedLon,
            acceptTerms,
            acceptDataPolicy,
          },
          selectedImage!,
          businessTypes
        );

        hideBizRegModal(modalRoot);

        if (result.ok) {
          form.reset();
          selectedImage = null;
          selectedLat = null;
          selectedLon = null;
          fileMeta.textContent = 'Ningún archivo';
          passwordInput.type = 'password';
          togglePwdBtn.textContent = 'Ver';
          setStatus(null);
          openSuccessModal(modalRoot, () => {
            form.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        } else {
          setStatus('error', result.message);
        }

        submitBtn.disabled = false;
      })();
    });
  });

  const panel = createEl('div', { className: 'sv-biz-reg', id: 'registro-empresa' }, [
    createEl('div', { className: 'sv-biz-reg__head' }, [
      createEl('h3', { className: 'sv-biz-reg__title' }, ['Registro de empresa']),
      createEl('p', { className: 'sv-biz-reg__lead' }, [
        'Completa el formulario para solicitar la activación de tu compañía en Servave. Los campos marcados con * son obligatorios.',
      ]),
    ]),
    form,
  ]);

  target.appendChild(panel);
}
